import { useState, useCallback } from 'react';
import { VersionResponse, VersionCreatePayload, VersionUpdatePayload } from '../types';
import { createVersion, updateVersion, fetchVersions } from '../api/upgradeApi';

interface UseVersionRegistryResult {
  versions: VersionResponse[];
  loading: boolean;
  saving: boolean;
  error: string | null;
  loadVersions: () => Promise<void>;
  addVersion: (payload: VersionCreatePayload) => Promise<void>;
  editVersion: (version: string, payload: VersionUpdatePayload) => Promise<void>;
  deprecateVersion: (version: string) => Promise<void>;
  clearError: () => void;
}

export function useVersionRegistry(): UseVersionRegistryResult {
  const [versions, setVersions] = useState<VersionResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const loadVersions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchVersions();
      setVersions(data.versions);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load versions');
    } finally {
      setLoading(false);
    }
  }, []);

  const addVersion = useCallback(async (payload: VersionCreatePayload) => {
    setSaving(true);
    setError(null);
    try {
      await createVersion(payload);
      await loadVersions();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create version';
      setError(msg);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [loadVersions]);

  const editVersion = useCallback(async (version: string, payload: VersionUpdatePayload) => {
    setSaving(true);
    setError(null);
    try {
      await updateVersion(version, payload);
      await loadVersions();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to update version';
      setError(msg);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [loadVersions]);

  const deprecateVersion = useCallback(async (version: string) => {
    setSaving(true);
    setError(null);
    try {
      await updateVersion(version, { is_deprecated: true });
      await loadVersions();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to deprecate version';
      setError(msg);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [loadVersions]);

  return {
    versions,
    loading,
    saving,
    error,
    loadVersions,
    addVersion,
    editVersion,
    deprecateVersion,
    clearError,
  };
}
